import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext({
  user: null, church: null, loading: true,
  login: async () => {}, registerChurch: async () => {},
  registerMember: async () => {}, logout: () => {}, refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [church, setChurch] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyTenantColors = useCallback((c) => {
    const root = document.documentElement;
    if (!c) {
      root.style.setProperty("--tenant-primary", "#7A1F2B");
      root.style.setProperty("--tenant-accent", "#C68A5B");
      return;
    }
    root.style.setProperty("--tenant-primary", c.primary_color || "#7A1F2B");
    root.style.setProperty("--tenant-accent", c.accent_color || "#C68A5B");
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ch_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setChurch(data.church);
      applyTenantColors(data.church);
    } catch (e) {
      localStorage.removeItem("ch_token");
      setUser(null); setChurch(null);
    } finally {
      setLoading(false);
    }
  }, [applyTenantColors]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, church_code) => {
    const { data } = await api.post("/auth/login", {
      email: (email || "").trim(),
      password,
      church_code: church_code ? church_code.trim() : church_code,
    });
    localStorage.setItem("ch_token", data.token);
    setUser(data.user); setChurch(data.church);
    applyTenantColors(data.church);
    return data;
  };

  const registerChurch = async (payload) => {
    const { data } = await api.post("/auth/register-church", payload);
    return data;
  };

  const registerMember = async (payload) => {
    const { data } = await api.post("/auth/register-member", payload);
    // Church may have auto_approve_members=false → server returns
    // status:"pending" and NO token. Bubble that up untouched so the
    // registration page can show the friendly "waiting on admin" screen.
    if (data.status === "pending") return data;
    localStorage.setItem("ch_token", data.token);
    setUser(data.user); setChurch(data.church);
    applyTenantColors(data.church);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("ch_token");
    setUser(null); setChurch(null);
    applyTenantColors(null);
  };

  return (
    <AuthCtx.Provider value={{ user, church, loading, login, registerChurch, registerMember, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

export const isSuper = (u) => u?.role === "super_admin";
export const isMainAdmin = (u) => u?.role === "main_admin";
export const isDeptAdmin = (u) => u?.role === "dept_admin";
export const canManageChurch = (u) => ["main_admin", "super_admin"].includes(u?.role);
